import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Settings,
    Info,
    Eye,
    Globe,
    Lock,
    FileText,
    CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import contentPagesService from '../../services/contentPages';

export default function AdminContentPageEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('content');

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        is_active: true,
        meta_title: '',
        meta_description: '',
        show_in_footer: true,
        show_in_header: false,
        // Not a stored field — a one-off instruction to the save endpoint to
        // bump content_version. Always starts off so a routine edit cannot
        // re-prompt every funeral home by accident.
        require_reacceptance: false
    });

    useEffect(() => {
        if (isEditing) {
            fetchPage();
        }
    }, [id]);

    const fetchPage = async () => {
        try {
            const response = await contentPagesService.getById(id);
            // Handle both direct object return or nested in a data property
            const data = response?.data || response;
            if (data && typeof data === 'object') {
                // require_reacceptance is a per-save instruction, never stored,
                // so it must not carry over from the loaded document.
                setFormData(prev => ({ ...prev, ...data, require_reacceptance: false }));
            } else {
                toast.error('Invalid page data received');
            }
        } catch (error) {
            console.error("Fetch page error:", error);
            toast.error('Failed to load page data');
            navigate('/admin/pages');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // Auto-generate slug from title if it's empty and we are typing title
            if (name === 'title' && !isEditing && !prev.slug) {
                newData.slug = value.toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)+/g, '');
            }
            return newData;
        });
    };

    const handleToggle = (name) => {
        setFormData(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.content) {
            toast.error('Title and Content are required');
            return;
        }

        try {
            setSaving(true);
            if (isEditing) {
                await contentPagesService.update(id, formData);
                toast.success('Page updated successfully');
            } else {
                await contentPagesService.create(formData);
                toast.success('Page created successfully');
            }
            navigate('/admin/pages');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save page');
        } finally {
            setSaving(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean'],
            ['blockquote', 'code-block']
        ],
    };

    if (loading) {
        return (
            <div className="flex h-[80vh] items-center justify-center">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-8 pb-24">
            {/* Action Bar - Improved Sticky positioning */}
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-md sticky top-0 z-20 py-4 -mx-4 lg:-mx-8 px-4 lg:px-8 border-b border-slate-200/60 shadow-sm transition-all duration-300">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full hover:bg-slate-100 transition-colors"
                        onClick={() => navigate('/admin/pages')}
                    >
                        <ArrowLeft size={20} className="text-slate-600" />
                    </Button>
                    <div>
                        <h1 className="text-lg font-bold text-slate-900 leading-tight">
                            {isEditing ? formData.title || 'Edit Page' : 'Create New Content Page'}
                        </h1>
                        <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wider">
                            {isEditing ? `System ID: ${id.substring(0, 8)}...` : 'New Resource Creation'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {isEditing && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 rounded-lg border-slate-200 hover:bg-slate-50"
                            onClick={() => window.open(`/pages/${formData.slug}`, '_blank')}
                        >
                            <Eye size={16} />
                            <span className="hidden sm:inline">Preview</span>
                        </Button>
                    )}
                    <Button
                        onClick={handleSubmit}
                        className="gap-2 bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-100 rounded-lg px-6"
                        disabled={saving}
                    >
                        {saving ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <Save size={18} />
                        )}
                        {isEditing ? 'Save Changes' : 'Publish Page'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <CardHeader className="bg-slate-50/50">
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <FileText size={16} /> Main Content
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6 space-y-6">
                            <div className="space-y-4 font-sans">
                                <div className="space-y-2">
                                    <Label htmlFor="title" className="text-slate-700 font-bold">Page Title</Label>
                                    <Input
                                        id="title"
                                        name="title"
                                        placeholder="e.g. About Our Mission"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="text-lg font-semibold h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="content" className="text-slate-700 font-bold">Body Content</Label>
                                    <div className="quill-editor-wrapper bg-white rounded-lg">
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.content}
                                            onChange={handleContentChange}
                                            modules={quillModules}
                                            className="min-h-[400px]"
                                            placeholder="Start writing your page content here..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <CardHeader className="bg-slate-50/50">
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <Globe size={16} /> Search Engine Optimization (SEO)
                            </CardTitle>
                            <CardDescription>Improve how this page appears in search results.</CardDescription>
                        </CardHeader>
                        <CardContent className="p-6 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="meta_title" className="text-slate-700 font-bold">SEO Title</Label>
                                <Input
                                    id="meta_title"
                                    name="meta_title"
                                    placeholder="Search result title..."
                                    value={formData.meta_title}
                                    onChange={handleChange}
                                    className="border-slate-200"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="meta_description" className="text-slate-700 font-bold">Meta Description</Label>
                                <Textarea
                                    id="meta_description"
                                    name="meta_description"
                                    placeholder="A brief summary for search engines (150-160 characters)..."
                                    value={formData.meta_description}
                                    onChange={handleChange}
                                    rows={3}
                                    className="border-slate-200"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card className="border-slate-200/60 shadow-sm overflow-hidden sticky top-[80px]">
                        <CardHeader className="bg-slate-50/80 pb-3 border-b border-slate-100">
                            <CardTitle className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 flex items-center gap-2">
                                <Settings size={14} className="text-slate-400" /> Page Settings
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-5 space-y-6">
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="slug" className="text-xs font-bold text-slate-700 uppercase tracking-tight">URL Slug</Label>
                                    <div className="flex items-center group">
                                        <div className="bg-slate-50 border border-r-0 border-slate-200 rounded-l-lg px-3 h-10 flex items-center text-slate-400 text-[10px] font-mono select-none">
                                            /pages/
                                        </div>
                                        <Input
                                            id="slug"
                                            name="slug"
                                            placeholder="page-url-path"
                                            value={formData.slug}
                                            onChange={handleChange}
                                            className="rounded-l-none rounded-r-lg font-mono text-xs border-slate-200 focus:ring-1 focus:ring-indigo-500/20 focus:border-indigo-500"
                                        />
                                    </div>
                                    <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1.5 leading-relaxed">
                                        <Info size={10} className="shrink-0" /> Permanent link for this resource.
                                    </p>
                                </div>

                                <div className="h-px bg-slate-100 mx-[-20px]" />

                                <div className="space-y-4 pt-1">
                                    <div className="flex items-center justify-between group">
                                        <div className="space-y-0.5">
                                            <Label className="text-xs font-bold text-slate-700 leading-tight">Live Status</Label>
                                            <p className="text-[10px] text-slate-400">Publicly accessible</p>
                                        </div>
                                        <Switch
                                            checked={formData.is_active}
                                            onCheckedChange={() => handleToggle('is_active')}
                                            className="data-[state=checked]:bg-emerald-500"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between group">
                                        <div className="space-y-0.5">
                                            <Label className="text-xs font-bold text-slate-700 leading-tight">Footer Link</Label>
                                            <p className="text-[10px] text-slate-400">Show in site footer</p>
                                        </div>
                                        <Switch
                                            checked={formData.show_in_footer}
                                            onCheckedChange={() => handleToggle('show_in_footer')}
                                            className="data-[state=checked]:bg-indigo-600"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between group">
                                        <div className="space-y-0.5">
                                            <Label className="text-xs font-bold text-slate-700 leading-tight">Header Link</Label>
                                            <p className="text-[10px] text-slate-400">Show in main nav</p>
                                        </div>
                                        <Switch
                                            checked={formData.show_in_header}
                                            onCheckedChange={() => handleToggle('show_in_header')}
                                            className="data-[state=checked]:bg-indigo-600"
                                        />
                                    </div>

                                    {/* Only meaningful on pages funeral homes accept; harmless
                                        elsewhere, so it is not conditioned on the slug. */}
                                    <div className="flex items-center justify-between group pt-1 border-t border-slate-100">
                                        <div className="space-y-0.5 pr-3">
                                            <Label className="text-xs font-bold text-slate-700 leading-tight">Material change</Label>
                                            <p className="text-[10px] text-slate-400 leading-relaxed">
                                                Require every funeral home to accept these terms again. Leave off for
                                                wording and typo fixes.
                                            </p>
                                        </div>
                                        <Switch
                                            checked={formData.require_reacceptance}
                                            onCheckedChange={() => handleToggle('require_reacceptance')}
                                            className="data-[state=checked]:bg-amber-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="p-5 bg-gradient-to-br from-indigo-50/50 to-white rounded-2xl border border-indigo-100/50 shadow-sm shadow-indigo-50/20">
                        <h4 className="flex items-center gap-2 text-indigo-900 font-bold text-[11px] uppercase tracking-wider mb-3">
                            <Info size={14} className="text-indigo-500" /> SEO Checklist
                        </h4>
                        <ul className="text-[11px] text-slate-600 space-y-2 list-none">
                            <li className="flex gap-2">
                                <CheckCircle size={12} className="text-indigo-400 shrink-0 mt-0.5" />
                                <span>Use <strong>H2/H3 headers</strong> for semantic structure.</span>
                            </li>
                            <li className="flex gap-2">
                                <CheckCircle size={12} className="text-indigo-400 shrink-0 mt-0.5" />
                                <span>Keep the <strong>Slug</strong> descriptive and hyphenated.</span>
                            </li>
                            <li className="flex gap-2">
                                <CheckCircle size={12} className="text-indigo-400 shrink-0 mt-0.5" />
                                <span>Fill <strong>Meta Description</strong> for search bots.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <style jsx global>{`
        .quill-editor-wrapper .ql-container {
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
            font-size: 15px;
            min-height: 400px;
            border: 1px solid #e2e8f0 !important;
            border-top: none !important;
            font-family: 'Inter', sans-serif;
            color: #1e293b;
        }
        .quill-editor-wrapper .ql-toolbar {
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            border: 1px solid #e2e8f0 !important;
            background: #f8fafc;
            padding: 12px !important;
        }
        .quill-editor-wrapper .ql-editor {
            min-height: 400px;
            padding: 24px !important;
        }
        .quill-editor-wrapper .ql-editor.ql-blank::before {
            left: 24px !important;
            font-style: normal;
            color: #94a3b8;
        }
        .quill-editor-wrapper .ql-editor img {
            max-width: 100%;
            height: auto;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
            margin: 24px 0;
        }
      `}</style>
        </div>
    );
}
